import React, { useCallback, useEffect, useState } from 'react'

import { classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { arrayOf, bool, func, number, shape, string } from 'prop-types'

import I18n from 'Pods/Shared/I18n'
import SharedOrderSummary from 'Pods/Shared/OrderSummary/OrderSummary'

import { sendExperimentEvent } from 'Utils/Experiments'
import formatPrice from 'Utils/formatPrice'
import roundHumanCount from 'Utils/roundHumanCount'
import { NOOP } from 'Utils/stableDefaults'

import PlanSelector from '../PlanSelector/PlanSelector'
import ItemContext from './Components/ItemContext/ItemContext'

const TRANSLATION_KEY_PREFIX = 'subscription_checkout.order_summary'
const PER_MODEL_COPY_EXPERIMENT_ID = 'checkout_per_model_copy'
const PER_MODEL_PRICE_UNIT_KEY = 'subscription_checkout.plan_selector.price_unit_per_model'

function OrderSummary({
  locale = 'en',
  planName,
  features,
  originalPrice = null,
  currentPrice,
  pricePerModel = null,
  vatAmount = 0,
  onPay = NOOP,
  disabled = true,
  hidePay = false,
  selectedPlanKey = null,
  subscriptionV16Enabled = false,
  subscriptionV17CatalogEnabled = false,
  isPremium = false,
  premiumRenewalDate = null,
  priceUnit = null,
  plans = null,
  onPlanChange = NOOP,
  perModelCopyEnabled = null,
  catalogValue = null,
  itemContext = null,
}) {
  const translate = useCallback(
    (key, options = {}) => I18n.t(key, { ns: 'rails', lng: locale, ...options }),
    [locale],
  )

  // The bucketed value is not applied during SSR prerender, so the server always renders
  // control. Gate the variant behind mount so SSR and the first client render match (no
  // hydration mismatch); the per-model copy swaps in after mount. Mirrors PlanSelector.
  const [isMounted, setIsMounted] = useState(false)
  useEffect(() => {
    setIsMounted(true)
  }, [])

  // PlanSelector owns exposure when the v1.6 plan selector renders; track here for the
  // single-plan card so the experiment fires exactly once and never double-counts.
  useEffect(() => {
    if (subscriptionV16Enabled && plans) return
    if (typeof perModelCopyEnabled !== 'boolean') return
    sendExperimentEvent(PER_MODEL_COPY_EXPERIMENT_ID, perModelCopyEnabled)
  }, [perModelCopyEnabled, subscriptionV16Enabled, plans])

  const subtotal = currentPrice
  const total = currentPrice + vatAmount

  const planKeyedTranslate = (suffix, options = {}) => {
    if (!selectedPlanKey) {
      return translate(`${TRANSLATION_KEY_PREFIX}.${suffix}`, options)
    }
    return translate(`${TRANSLATION_KEY_PREFIX}.plans.${selectedPlanKey}.${suffix}`, options)
  }

  // Single-plan card only; the v1.6 plan selector owns the per-model copy in its own path.
  const isSinglePlanCard = !(subscriptionV16Enabled && plans)
  // subscription_v1_7: the backend sends the tier-aware title/price/unit/bullets; render them
  // verbatim instead of the FE plan-keyed copy. Premium always takes this path (its checkout is
  // single-plan and tier-driven), even if the v1.6 multi-plan selector is later re-enabled for
  // essentials — `isPremium` (backend tier) keeps premium on the backend-copy path while the
  // selector stays scoped to essentials.
  const useV17Copy = subscriptionV17CatalogEnabled && (isPremium || isSinglePlanCard)

  const displayedPlanName = useV17Copy || !selectedPlanKey
    ? planName
    : planKeyedTranslate('name')

  const billingPeriodLabel = planKeyedTranslate('billing_period')
  const showPerModelCopy = !useV17Copy
    && isSinglePlanCard && isMounted && perModelCopyEnabled === true && pricePerModel != null
  const displayedPrice = showPerModelCopy ? pricePerModel : currentPrice
  const priceUnitLabel = (() => {
    if (useV17Copy) return priceUnit || planKeyedTranslate('price_unit')
    if (showPerModelCopy) return translate(PER_MODEL_PRICE_UNIT_KEY)
    return planKeyedTranslate('price_unit')
  })()

  const featureSlots = ['first', 'second', 'third']

  const formattedCatalogValue = catalogValue ? roundHumanCount(catalogValue) : ''

  const planFeatures = useV17Copy || !selectedPlanKey
    ? features
    : featureSlots.map((slot) => (
      translate(`${TRANSLATION_KEY_PREFIX}.plans.${selectedPlanKey}.features.${slot}`, {
        modelCount: formattedCatalogValue,
      })
    ))

  const planItemCard = (
    <div className={classnames('order-summary__plan', subscriptionV16Enabled && 'order-summary__plan--outlined')}>
      <div className="order-summary__plan-content">
        <span className="cgt-body-base-semibold">{displayedPlanName}</span>
        <ul className="order-summary__features">
          {planFeatures.map((feature) => (
            <li key={feature} className="order-summary__feature">
              <span className="order-summary__feature-bullet" />
              <span className="order-summary__feature-text cgt-caption">{feature}</span>
            </li>
          ))}
        </ul>
      </div>
      <div className="order-summary__plan-pricing">
        {billingPeriodLabel && (
          <span className="order-summary__billing-period cgt-caption-semibold">
            {billingPeriodLabel}
          </span>
        )}
        <div className="order-summary__price-wrap">
          {originalPrice && !showPerModelCopy && (
            <span className="order-summary__original-price cgt-body-base-semibold">
              {formatPrice(originalPrice)}
            </span>
          )}
          <span className="order-summary__current-price cgt-body-lg-semibold">
            {formatPrice(displayedPrice)}
          </span>
        </div>
        <span className="order-summary__price-unit cgt-caption-semibold">
          {priceUnitLabel}
        </span>
      </div>
    </div>
  )

  const legal = (
    <>
      {translate(`${TRANSLATION_KEY_PREFIX}.legal_agreement_prefix`)}{' '}
      <a
        className="cgt-link-primary-accented"
        href="https://www.cgtrader.com/pages/terms-and-conditions"
        target="_blank"
        rel="noreferrer"
      >
        {translate(`${TRANSLATION_KEY_PREFIX}.legal_agreement_terms`)}
      </a>
      ,{' '}
      <a
        className="cgt-link-primary-accented"
        href="https://www.cgtrader.com/pages/privacy-policy"
        target="_blank"
        rel="noreferrer"
      >
        {translate(`${TRANSLATION_KEY_PREFIX}.legal_agreement_privacy`)}
      </a>
      {', '}{translate(`${TRANSLATION_KEY_PREFIX}.legal_agreement_connector`)}{' '}
      <a
        className="cgt-link-primary-accented"
        href="https://www.cgtrader.com/pages/cgtrader-subscription-terms"
        target="_blank"
        rel="noreferrer"
      >
        {translate(`${TRANSLATION_KEY_PREFIX}.legal_agreement_fair_use`)}
      </a>
      .
    </>
  )

  // Premium-only: the upgrade CTA + the "unlocks immediately" footer note. Essentials keeps the
  // plain pay button and no note.
  const payLabel = translate(
    `${TRANSLATION_KEY_PREFIX}.${isPremium ? 'pay_button_upgrade' : 'pay_button'}`,
    { amount: formatPrice(total) },
  )
  // Only an Essentials -> Premium upgrade carries a renewal date (Premium unlocks now, the new
  // price starts from the active sub's renewal date). Fresh Premium purchases bill today, so the
  // backend sends no date and the note is hidden.
  const premiumRenewalNote = isPremium && premiumRenewalDate
    ? (
      <span className="cgt-caption">
        {translate(`${TRANSLATION_KEY_PREFIX}.premium_renewal_note`, {
          price: formatPrice(currentPrice),
          date: premiumRenewalDate,
        })}
      </span>
    )
    : null

  return (
    <SharedOrderSummary
      title={translate(`${TRANSLATION_KEY_PREFIX}.title`)}
      subtotal={subtotal}
      subtotalLabel={translate(`${TRANSLATION_KEY_PREFIX}.subtotal`)}
      vatAmount={vatAmount}
      vatLabel={translate(`${TRANSLATION_KEY_PREFIX}.vat_included`)}
      vatTooltip={translate(`${TRANSLATION_KEY_PREFIX}.vat_tooltip`)}
      total={total}
      totalLabel={translate(`${TRANSLATION_KEY_PREFIX}.amount_due_today`)}
      payLabel={payLabel}
      onPay={onPay}
      disabled={disabled}
      hidePay={hidePay}
      legal={legal}
      footer={premiumRenewalNote}
    >
      {itemContext?.thumbnail && itemContext?.modelName && (
        <ItemContext
          thumbnail={itemContext.thumbnail}
          modelName={itemContext.modelName}
          subLabel={translate(`${TRANSLATION_KEY_PREFIX}.item_context.sub_label`)}
        />
      )}
      {subscriptionV16Enabled && plans && (
        <PlanSelector
          plans={plans}
          selectedKey={selectedPlanKey}
          onChange={onPlanChange}
          locale={locale}
          perModelCopyEnabled={perModelCopyEnabled}
        />
      )}
      {planItemCard}
    </SharedOrderSummary>
  )
}

OrderSummary.propTypes = {
  locale: string,
  planName: string.isRequired,
  features: arrayOf(string).isRequired,
  originalPrice: number,
  currentPrice: number.isRequired,
  pricePerModel: number,
  vatAmount: number,
  onPay: func,
  disabled: bool,
  hidePay: bool,
  selectedPlanKey: string,
  subscriptionV16Enabled: bool,
  subscriptionV17CatalogEnabled: bool,
  isPremium: bool,
  premiumRenewalDate: string,
  priceUnit: string,
  plans: arrayOf(shape({ key: string, price: number })),
  onPlanChange: func,
  perModelCopyEnabled: bool,
  catalogValue: string,
  itemContext: shape({ thumbnail: string, modelName: string }),
}

export default OrderSummary
